import type { NotificationEvent } from '../types/notifications.types';

/**
 * Hard ceiling on how many notification rows the feed keeps in memory.
 *
 * The page is a live, top-anchored feed. `useInfiniteQuery` runs on
 * react-query v3 (no built-in `maxPages`), so `data.pages` would grow
 * without bound as "Load older" is clicked, while the live overlay
 * triggers a feed rebuild on every ~100ms coalesce tick. Capping the
 * MERGED feed bounds, in one place, the virtualizer count, its DOM
 * measurement cache, the per-tick rebuild cost, and the retained-object
 * set. Older rows stay in Mongo and are reachable via the time-range /
 * Pair / Rule filters — the feed's primary "go back" controls.
 */
export const MAX_FEED_ROWS = 1000;

/**
 * Merge the live WS overlay with the react-query REST pages into a single
 * newest-first feed, deduped by `id` and capped at `max` rows.
 *
 * Both inputs are ALREADY sorted descending by the page's canonical key
 * (occurredAt desc, then per-rule `seq` desc): the overlay maintains that
 * order via ordered insert, and each REST page comes back desc with pages
 * appended newest→older. So this is a linear two-way merge rather than a
 * full re-sort of the whole combined array on every live tick — the old
 * hot-path cost that grew with every loaded page.
 *
 * On an id collision (an event delivered via BOTH the realtime channel and
 * a REST refetch) the overlay copy wins, preserving the previous
 * "live overlay first" dedupe.
 */
export function mergeNotificationFeed(
  overlay: NotificationEvent[],
  pages: NotificationEvent[][],
  compareDesc: (a: NotificationEvent, b: NotificationEvent) => number,
  max: number = MAX_FEED_ROWS,
): NotificationEvent[] {
  const seen = new Set<string>();
  const out: NotificationEvent[] = [];

  let oi = 0;
  // Walk the pages as one flattened stream without materialising it.
  let pi = 0;
  let pj = 0;
  const peekPage = (): NotificationEvent | undefined => {
    while (pi < pages.length) {
      const page = pages[pi];
      if (pj < page.length) return page[pj];
      pi += 1;
      pj = 0;
    }
    return undefined;
  };

  while (out.length < max) {
    const o = overlay[oi];
    const p = peekPage();
    if (o === undefined && p === undefined) break;

    let pick: NotificationEvent;
    if (o === undefined) {
      pick = p as NotificationEvent;
      pj += 1;
    } else if (p === undefined || compareDesc(o, p) <= 0) {
      // `<= 0` ⇒ the overlay event sorts first (newer or equal); on a tie
      // the overlay copy is chosen so it wins the dedupe below.
      pick = o;
      oi += 1;
    } else {
      pick = p;
      pj += 1;
    }

    if (!seen.has(pick.id)) {
      seen.add(pick.id);
      out.push(pick);
    }
  }

  return out;
}
